import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  useTheme,
  Tooltip,
  Collapse,
  TablePagination,
} from '@mui/material';

import {
    Delete,
    Edit,
    Email,
    FileUpload,
  LocationOn,
  Person,
  Search,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DumpLocation, LoadLocation, LoadLocationForm, Material, Meta } from '../../types';
import { loadLocationApi, materialApi, dumpLocationApi } from '../../services/api';
import DumpLocationFormModalProps from '../../components/AddNewDumpLocationPopUp';
import { unwrapListResponse, unwrapMetaResponse } from '../../apiUnwrapper/apiUnwrapper';
import { useSnackbar } from 'notistack';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  PageHeader,
} from '../../components/SharedPageParts';
import { useAuth } from '../../services/AuthContext';

const DumpLocationsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageLocations = user?.roleName === 'Admin';
  const [open, setOpen] = useState(false);

  const [dumpLocations, setDumpLocations] = useState<DumpLocation[]>([]);
  const [dumpLocationsMeta, setDumpLocationsMeta] = useState<Meta>({ total: 0, size: 5, page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [fileName, setFileName] = useState('');
  const [openMaterialTonnage, setOpenMaterialTonnage] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [selectedLocation, setSelectedLocation] = useState<DumpLocation | undefined>(undefined);
  const { enqueueSnackbar } = useSnackbar();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});
  const [confirmMessage, setConfirmMessage] = useState("Are you sure?");

  const openCreateModal = () => {
    setMode("create");
    setSelectedLocation({
      id: 0,
      level1: "",
      level2: "",
      level3: "",
      level4: "",
      isActive: true
    });
    setOpen(true);
  };

  const openEditModal = (loc: DumpLocation) => {
    setMode("edit");
    setSelectedLocation({
      id: loc.id,
      level1: loc.level1,
      level2: loc.level2,
      level3: loc.level3,
      level4: loc.level4,
      isActive: loc.isActive
    });
    setOpen(true);
  };

  const handleSubmit = async (data: DumpLocation) => {
    try{
      if (mode === "create") {
        await dumpLocationApi.createDumpLocation(data);
      } else {
        await dumpLocationApi.updateDumpLocation(data);
      }
      enqueueSnackbar(`Dump Location ${mode === "create" ? "created" : "updated"} successfully`, { variant: 'success' });
      await reloadlocations();
    } catch (err) {
      enqueueSnackbar(`Failed to ${mode === "create" ? "create" : "update"} Dump Location`, { variant: 'error' });
      console.error(`Error ${mode === "create" ? "creating" : "updating"} Dump Location:`, err);
    } finally { 
      setOpen(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setFileName(file.name);
        // You can also upload or parse the file here
      }
  };
  useEffect(() => {
      reloadlocations();
   }, []);
        
          const reloadlocations = async (page = 1, size = 5) => {
            try {
              setLoading(true);
              setError(null);
              const dumpLocationsData = await dumpLocationApi.getDumpLocations(page, size);
              setDumpLocations(unwrapListResponse<DumpLocation>(dumpLocationsData));
              setDumpLocationsMeta(unwrapMetaResponse<Meta>(dumpLocationsData) || { total: 0, size: 5, page: 1 });
            } catch (err) {
              setError('Failed to load dump locations. Make sure the Spring Boot backend is running on port 8080.');
              console.error('Error loading dump locations:', err);
            } finally {
              setLoading(false);
            }
          };

          const filteredDumpLocations = dumpLocations.filter(dumpLocation =>
            dumpLocation.level1.toLowerCase().includes(searchTerm.toLowerCase()) ||
            dumpLocation.level2.toLowerCase().includes(searchTerm.toLowerCase()) ||
            dumpLocation.level3.toLowerCase().includes(searchTerm.toLowerCase()) ||
            dumpLocation.level4.toLowerCase().includes(searchTerm.toLowerCase())
          );
  function handleDelete(id: number): void {
    try{
              setConfirmAction(() => async () => {
              await dumpLocationApi.deleteDumpLocation(id);
              enqueueSnackbar("Dump Location deleted successfully", { variant: "success" });
              reloadlocations(dumpLocationsMeta.page, dumpLocationsMeta.size);
            });
            setConfirmMessage("Are you sure you want to delete this Dump Location?");
            setOpenConfirm(true);
          }
          catch(error){
            enqueueSnackbar("Error deleting Dump Location: "+error , { variant: "error" });
            setError('Error deleting Dump Location: '+error );
          }
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      {/* Main Content for Dump Locations Page can be added here */}
      <Box className="MuiBox-root-margin" sx={{ px: 1, mx: 'auto' }}>
      <PageHeader
                                              title="Dump Locations Management"
                                              Icon={LocationOn}
                                              onBack={() => navigate('/haulmasterlive')}
                                              backLabel="Back to Haul Master"
                                              subtitle="Manage dumping sites"
                                            />

        {error && (
        <Alert severity="error" sx={{ mb: 3, backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, border: '1px solid #ef4444' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search operators by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
                sx: { backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }
              }}
              sx={{ 
                flexGrow: 1, 
                minWidth: 250,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: theme.palette.text.secondary },
                  '&.Mui-focused fieldset': { borderColor: '#667eea' },
                },
                '& .MuiInputBase-input': { color: theme.palette.text.primary }
              }}
            />
            {canManageLocations && (
              <>
            <Button variant="contained" sx={{
           background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
        }} onClick={() => openCreateModal()}>Add New Dump Location</Button>
            <DumpLocationFormModalProps open={open} onClose={() => setOpen(false)} onSubmit={handleSubmit} mode={mode} initialData={selectedLocation} />

      <Button
        variant="contained"
        component="label"
        startIcon={<FileUpload />}
        sx={{
           background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
        }}
      >
        Bulk Upload
        <input
          type="file"
          accept=".csv"
          hidden
          onChange={handleFileChange}
        />
      </Button>
      {fileName && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Selected file: <strong>{fileName}</strong>
        </Typography>
      )}
              </>
            )}

          </Box>
        </CardContent>
      </Card>

      <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
            Dump Locations ({dumpLocationsMeta.total})
          </Typography>
          <TablePagination
                                component="div"
                                count={dumpLocationsMeta.total}
                                page={dumpLocationsMeta.page - 1}
                                onPageChange={(e, newPage) => reloadlocations(newPage + 1, dumpLocationsMeta.size)}
                                rowsPerPage={dumpLocationsMeta.size}
                                onRowsPerPageChange={(e) => reloadlocations(1, parseInt(e.target.value, 10))}
                                rowsPerPageOptions={[5, 10, 15, 20]}
                                labelRowsPerPage="Dump locations per page"
                              />
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: theme.palette.background.default, borderColor: theme.palette.divider }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#2d3e50' }}>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Level 1</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Level 2</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Level 3</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Level 4</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Status</TableCell>
                  {canManageLocations && (
                    <TableCell align="center" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDumpLocations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canManageLocations ? 6 : 5} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Person sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
                          No operators found
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          {searchTerm ? 'Try adjusting your search terms' : 'Start the Spring Boot backend to load dump locations'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDumpLocations.map((dumpLocation) => (
                    <TableRow key={dumpLocation.id} hover sx={{ '&:hover': { backgroundColor: theme.palette.background.paper }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>{dumpLocation.level1}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>{dumpLocation.level2}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>{dumpLocation.level3}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>{dumpLocation.level4}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Chip label={dumpLocation.isActive? "Active" : "Inactive"} size="small" />
                      </TableCell>
                      {canManageLocations && (
                      <TableCell align="center" sx={{ borderBottom: 'none' }}>
                                              <IconButton size="small" sx={{ color: '#06beb6' }}
                                                onClick={() => {
                                                openEditModal(dumpLocation);
                                                }}>
                                              <Edit />
                                              </IconButton>
                                              <IconButton
                                                key={dumpLocation.id}
                                                size="small"
                                                sx={{ color: '#ef4444' }}
                                                onClick={() => handleDelete(dumpLocation.id)} // ✅ pass unit.id
                                              >
                                                <Delete />
                                              </IconButton>
                                            </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
                                component="div"
                                count={dumpLocationsMeta.total}
                                page={dumpLocationsMeta.page - 1}
                                onPageChange={(e, newPage) => reloadlocations(newPage + 1, dumpLocationsMeta.size)}
                                rowsPerPage={dumpLocationsMeta.size}
                                onRowsPerPageChange={(e) => reloadlocations(1, parseInt(e.target.value, 10))}
                                rowsPerPageOptions={[5, 10, 15, 20]}
                                labelRowsPerPage="Dump locations per page"
                              />
          <ConfirmDialog
                              open={openConfirm}
                              onClose={() => setOpenConfirm(false)}
                              onConfirm={confirmAction}
                              title="Confirm Delete"
                              message={confirmMessage}
                            />
        </CardContent>
      </Card>
      </Box>
    </Box>
  );
};

export default DumpLocationsPage;